import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import OrbitalScroll from "@/components/sections/OrbitalScroll";
import HeroVideoSlider from "@/components/sections/HeroVideoSlider";
import Marquee from "@/components/Marquee";
import FAQSection from "@/components/FAQSection";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Arrow120 } from "@/components/Arrow120";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const HomeCreatives = ({ items }) => (
  <section className="py-24 md:py-32" data-testid="home-creatives">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-3">Selected work</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-ahm-teal text-balance">
            A small <em className="not-italic text-ahm-orange">obsession</em>
            <br /> with the craft.
          </h2>
        </div>
        <Link to="/creatives" className="btn-pill-outline group" data-testid="home-creatives-see-more">
          See more
          <Arrow120 />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {items.slice(0, 4).map((it, i) => (
          <motion.div
            key={it.id || i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.08 }}
            className={`group relative overflow-hidden rounded-3xl bg-ahm-muted ${i % 3 === 0 ? "md:h-[60vh]" : "md:h-[50vh]"}`}
          >
            <Link to={`/creatives/${it.category}`}>
              <img src={it.image} alt={it.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ahm-teal/85 via-ahm-teal/20 to-transparent opacity-90" />
              <div className="absolute bottom-6 left-6 right-6 text-ahm-cream">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-ahm-yellow">{String(i + 1).padStart(2, "0")} · Category</p>
                <h3 className="font-display text-3xl md:text-5xl mt-2 leading-tight">{it.title}</h3>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const ServicesSection = ({ services }) => {
  const [openIdx, setOpenIdx] = useState(null);
  // Curated placeholder images so admin doesn't have to add one per service immediately.
  const images = [
    "https://images.unsplash.com/photo-1611605698335-8b1569810432?w=1200&q=85",
    "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1200&q=85",
    "https://images.unsplash.com/photo-1492724441997-5dc865305da7?w=1200&q=85",
    "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&q=85",
    "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200&q=85",
    "https://images.unsplash.com/photo-1561070791-2526d30994b8?w=1200&q=85",
  ];
  return (
    <section className="py-24 md:py-32 bg-ahm-muted/60" data-testid="home-services">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-12 md:mb-16 items-end">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-3">Services</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.95] text-ahm-teal text-balance">
              One studio. <em className="not-italic text-ahm-orange">Every</em> creative discipline.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-12">
            <p className="text-base md:text-lg leading-relaxed text-ahm-teal/80">
              We're a full-service creative agency. Strategy, design, film, performance — under one roof, executed by humans who actually care.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {services.map((s, i) => {
            const isOpen = openIdx === i;
            return (
              <button
                key={s.id || i}
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : i)}
                data-testid={`service-card-${i}`}
                aria-pressed={isOpen}
                className={`group relative aspect-[3/4] sm:aspect-[4/5] rounded-2xl overflow-hidden bg-ahm-teal text-left focus:outline-none focus:ring-2 focus:ring-ahm-orange focus:ring-offset-2 focus:ring-offset-ahm-cream ${
                  isOpen ? "is-open" : ""
                }`}
              >
                <img
                  src={s.image || images[i % images.length]}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-110 group-[.is-open]:scale-110"
                />
                <div className="absolute inset-0 bg-ahm-teal/0 group-hover:bg-ahm-teal/70 group-[.is-open]:bg-ahm-teal/70 transition-colors duration-500" />
                <span className="absolute top-3 left-3 md:top-5 md:left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-ahm-cream/85 bg-ahm-teal/70 px-2 py-1 rounded-full">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="absolute inset-0 flex items-end p-4 md:p-7">
                  <h3
                    className="font-display text-2xl md:text-4xl lg:text-5xl text-ahm-cream leading-[0.95] opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 group-[.is-open]:opacity-100 group-[.is-open]:translate-y-0 transition-all duration-500"
                    data-testid={`service-card-title-${i}`}
                  >
                    {s.title}
                  </h3>
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-10 text-sm text-ahm-teal/55 italic">Hover (desktop) or tap (mobile) any tile to reveal the service.</p>
      </div>
    </section>
  );
};

const CTABanner = ({ text }) => (
  <section className="py-20 md:py-28 bg-ahm-teal text-ahm-cream" data-testid="cta-banner">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-10">
      <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-balance max-w-3xl">
        {text} <span className="text-ahm-yellow">?</span>
      </h2>
      <Link to="/contact" className="btn-pill bg-ahm-orange hover:bg-ahm-yellow hover:text-ahm-teal group whitespace-nowrap" data-testid="cta-banner-btn">
        Contact us
        <Arrow120 />
      </Link>
    </div>
  </section>
);

const VideoCarousel = ({ videos }) => {
  const [idx, setIdx] = useState(0);
  if (!videos.length) return null;
  const next = () => setIdx((i) => (i + 1) % videos.length);
  const prev = () => setIdx((i) => (i - 1 + videos.length) % videos.length);
  const visible = [0, 1, 2].map((o) => videos[(idx + o) % videos.length]);
  return (
    <section className="py-24 md:py-32 bg-ahm-cream" data-testid="video-carousel">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-3">Reels & films</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-ahm-teal">
              Watch us <em className="not-italic text-ahm-orange">work</em>.
            </h2>
          </div>
          <div className="flex gap-3">
            <button onClick={prev} className="w-14 h-14 rounded-full border border-ahm-teal/30 text-ahm-teal hover:bg-ahm-teal hover:text-ahm-cream transition" data-testid="video-prev">
              <ChevronLeft className="mx-auto" />
            </button>
            <button onClick={next} className="w-14 h-14 rounded-full border border-ahm-teal/30 text-ahm-teal hover:bg-ahm-teal hover:text-ahm-cream transition" data-testid="video-next">
              <ChevronRight className="mx-auto" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visible.map((v, i) => (
            <div key={`${v.id}-${i}`} className="rounded-2xl overflow-hidden aspect-video bg-ahm-teal/10 shadow-xl">
              <iframe
                src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1&mute=1&loop=1&playlist=${v.youtube_id}&controls=0&modestbranding=1&rel=0`}
                title={v.title}
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { config } = useSite();
  const [circles, setCircles] = useState([]);
  const [services, setServices] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [logos, setLogos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [heroVideos, setHeroVideos] = useState([]);

  useEffect(() => {
    api.get("/home_circles").then((r) => setCircles(r.data));
    api.get("/services").then((r) => setServices(r.data));
    api.get("/home_creatives").then((r) => setCreatives(r.data));
    api.get("/client_logos").then((r) => setLogos(r.data));
    api.get("/videos").then((r) => setVideos(r.data));
    api.get("/hero_videos").then((r) => setHeroVideos(r.data)).catch(() => setHeroVideos([]));
  }, []);

  return (
    <div data-testid="home-page">
      <HeroVideoSlider videos={heroVideos} kicker={config.hero_kicker} />

      <OrbitalScroll items={circles} />

      {/* Banner */}
      <section className="py-20 md:py-28 border-y border-ahm-teal/15" data-testid="main-banner">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="font-display italic text-3xl md:text-5xl lg:text-6xl text-ahm-teal leading-tight text-balance">
            &ldquo;{config.main_banner_text}&rdquo;
          </p>
        </div>
      </section>

      <ServicesSection services={services} />

      <CTABanner text={config.cta_banner_text || "Lacking creative design at your company"} />

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold text-center mb-6">Trusted by ambitious teams</p>
        </div>
        <Marquee logos={logos} />
      </section>

      <HomeCreatives items={creatives} />

      <VideoCarousel videos={videos} />

      <FAQSection page="home" />
    </div>
  );
};

export default Home;
